import React, { useState, useRef, useEffect } from 'react';

import styles from './MatrixQuestionEditScale.module.css';

import tickIcon from '../../../../../../../../assets/img/tick.svg';
import closeIcon from '../../../../../../../../assets/img/delete.svg';

import AxisScaleFields from './components/QuestionChoiceTagging/AxisScaleFields';
import ToggleSelector from '../../../QuestionsColumn/components/SidePropertiesContainer/components/ToggleSelector/ToggleSelector';
import getMatrixScaleLabels, {
  getMatrixNeutralLabel
} from '../../../../../../../../../users/helpers/getMatrixScaleLabels';
import {
  DECISION_FRAMEWORK_TYPES,
  MATRIX_QUESTION_CUSTOM_SCALE_TYPES,
  MATRIX_SCALE_LENGTH_WITH_NEUTRAL_MIDPOINT
} from '../../../../helpers/constants';

const NEUTRAL_MIDPOINT_INDEX = 2;

const isScaleIncomplete = labels =>
  labels &&
  labels.length &&
  (labels.length < 4 || labels.some(label => !label || !label.trim().length));

export default function({ actions, surveyBuilderPopup }) {
  const [newSessionValues, setNewSessionValues] = useState({});

  const { question, surveyLanguage } = surveyBuilderPopup;

  const optionsContainerRef = useRef();

  const hasYAxis =
    question &&
    question.matrix &&
    question.matrix.decisionFrameworkType !== DECISION_FRAMEWORK_TYPES.SINGLE;
  const scalePropertyNames = hasYAxis
    ? ['xScaleLabels', 'yScaleLabels']
    : ['xScaleLabels'];

  const hasNeutralMidpoint =
    !!newSessionValues.xScaleLabels &&
    newSessionValues.xScaleLabels.length ===
      MATRIX_SCALE_LENGTH_WITH_NEUTRAL_MIDPOINT;

  useEffect(() => {
    // Set scale default values if there are no saved custom values
    if (question && question.matrix && question.matrix.decisionFrameworkType) {
      let newScale;
      if (
        question.matrix &&
        question.matrix.customScale &&
        question.matrix.customScale.type ===
          MATRIX_QUESTION_CUSTOM_SCALE_TYPES.LIKERT_SCALE
      ) {
        newScale = {
          xScaleLabels: question.matrix.customScale.xScaleLabels,
          yScaleLabels: question.matrix.customScale.yScaleLabels
        };
      } else {
        newScale = getMatrixScaleLabels(
          question.matrix.decisionFrameworkType,
          surveyLanguage
        );
      }

      setNewSessionValues(newScale);
    }
  }, []);

  const onConfirm = () => {
    const questionMatrix = { ...question.matrix };

    questionMatrix.customScale = {
      ...(question.customScale || {}),
      type: MATRIX_QUESTION_CUSTOM_SCALE_TYPES.LIKERT_SCALE,
      xScaleLabels: newSessionValues.xScaleLabels,
      yScaleLabels: newSessionValues.yScaleLabels
    };

    actions.updateQuestionProperty('matrix', questionMatrix);

    /*
    selectAtMost holds the number of scale options of a matrix question: the
    results graph normalises the answer positions over selectAtMost - 1, so it
    follows the scale and becomes 5 with a neutral midpoint and 4 without it
    */
    const scaleLength = Math.max(
      ...scalePropertyNames.map(name => (newSessionValues[name] || []).length)
    );
    const selectAtMost = scaleLength || question.selectAtMost;

    if (selectAtMost !== question.selectAtMost) {
      actions.updateQuestionProperty('selectAtMost', selectAtMost);
    }

    const questionToSetAsActiveFlow = {
      ...question,
      matrix: questionMatrix,
      selectAtMost
    };

    actions.setActiveFlow(questionToSetAsActiveFlow);

    actions.toggleSurveyBuilderPopup(null);
  };

  const onAxisScaleFieldChangeValue = (
    scalePropertyName,
    fieldIndex,
    value
  ) => {
    const scaleToUpdate = [...newSessionValues[scalePropertyName]];
    scaleToUpdate[fieldIndex] = value;

    setNewSessionValues({
      ...newSessionValues,
      [scalePropertyName]: scaleToUpdate
    });
  };

  // Both axes get the neutral midpoint, so they keep the same scale
  const onNeutralMidpointChange = addNeutralMidpoint => {
    const neutralLabel = getMatrixNeutralLabel(surveyLanguage);

    const updatedScales = scalePropertyNames.reduce((acc, name) => {
      const labels = [...(newSessionValues[name] || [])];

      if (
        addNeutralMidpoint &&
        labels.length < MATRIX_SCALE_LENGTH_WITH_NEUTRAL_MIDPOINT
      ) {
        labels.splice(NEUTRAL_MIDPOINT_INDEX, 0, neutralLabel);
      }
      if (
        !addNeutralMidpoint &&
        labels.length === MATRIX_SCALE_LENGTH_WITH_NEUTRAL_MIDPOINT
      ) {
        labels.splice(NEUTRAL_MIDPOINT_INDEX, 1);
      }

      return { ...acc, [name]: labels };
    }, {});

    setNewSessionValues({ ...newSessionValues, ...updatedScales });
  };

  const isConfirmVisible = () =>
    !scalePropertyNames.some(name => isScaleIncomplete(newSessionValues[name]));

  return (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        <div className={styles.top}>
          <div className={styles.title}>Edit scale</div>
        </div>
        <div
          ref={optionsContainerRef}
          className={styles.axisScaleFieldContainers}
        >
          <AxisScaleFields
            title="Scale x-axis"
            values={newSessionValues.xScaleLabels}
            onValueChange={(fieldIndex, value) =>
              onAxisScaleFieldChangeValue('xScaleLabels', fieldIndex, value)
            }
          />
          {hasYAxis ? (
            <AxisScaleFields
              title="Scale y-axis"
              values={newSessionValues.yScaleLabels}
              onValueChange={(fieldIndex, value) =>
                onAxisScaleFieldChangeValue('yScaleLabels', fieldIndex, value)
              }
            />
          ) : null}
        </div>
        {newSessionValues.xScaleLabels ? (
          <div className={styles.neutralMidpoint}>
            <ToggleSelector
              label="Add neutral midpoint"
              value={hasNeutralMidpoint}
              onChange={onNeutralMidpointChange}
              labelPositionRight
              customParentStyle={styles.neutralMidpointToggle}
              customLabelStyle={styles.neutralMidpointToggleLabel}
            />
          </div>
        ) : null}
      </div>
      <div className={styles.footer}>
        <div />
        <div className={styles.actions}>
          {isConfirmVisible() ? (
            <img
              className={`${
                styles.confirm
              } workspaces-manage-workspace-confirm-button`}
              src={tickIcon}
              alt="Confirm icon"
              onClick={() => onConfirm()}
              role="presentation"
            />
          ) : null}
          <img
            className={styles.close}
            src={closeIcon}
            alt="Close icon"
            onClick={() => actions.toggleSurveyBuilderPopup(null)}
            role="presentation"
          />
        </div>
      </div>
    </div>
  );
}
